import { useCallback, useRef, useState } from 'react'

interface PermissionDialogState {
  open: boolean
  expiresAt: string
}

/**
 * Hook for programmatically controlling the TrialTimelineView and its
 * associated grant permission dialog.
 *
 * @example
 * ```tsx
 * const timeline = useTrialTimelineDialog()
 *
 * // Open timeline
 * timeline.openTimeline()
 *
 * // After successful trial start, show permission dialog
 * await timeline.openPermissionDialog(expiresAt)
 *
 * // Render with controlled props
 * <TrialTimelineView {...timeline.timelineProps} ... />
 * ```
 */
export function useTrialTimelineDialog() {
  const [isTimelineOpen, setIsTimelineOpen] = useState(false)
  const [permissionDialogState, setPermissionDialogState] =
    useState<PermissionDialogState>({
      open: false,
      expiresAt: '',
    })

  // Store the resolve function for the permission dialog promise
  const resolveRef = useRef<(() => void) | null>(null)

  const openTimeline = useCallback(() => {
    setIsTimelineOpen(true)
  }, [])

  const closeTimeline = useCallback(() => {
    setIsTimelineOpen(false)
  }, [])

  const onTimelineOpenChange = useCallback((open: boolean) => {
    setIsTimelineOpen(open)
  }, [])

  /**
   * Opens the grant permission dialog and returns a promise that resolves
   * when the user either accepts or dismisses the dialog.
   */
  const openPermissionDialog = useCallback(
    (expiresAt: string): Promise<void> => {
      return new Promise((resolve) => {
        resolveRef.current = resolve
        setPermissionDialogState({
          open: true,
          expiresAt,
        })
      })
    },
    [],
  )

  const closePermissionDialog = useCallback(() => {
    setPermissionDialogState((prev) => ({
      ...prev,
      open: false,
    }))
    resolveRef.current?.()
    resolveRef.current = null
  }, [])

  const onPermissionDialogOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        closePermissionDialog()
      }
    },
    [closePermissionDialog],
  )

  return {
    // Timeline state
    isTimelineOpen,
    openTimeline,
    closeTimeline,

    // Permission dialog state
    isPermissionDialogOpen: permissionDialogState.open,
    permissionDialogExpiresAt: permissionDialogState.expiresAt,
    openPermissionDialog,
    closePermissionDialog,

    /**
     * Convenience props object to spread on TrialTimelineView.
     * Use with: `<TrialTimelineView {...timeline.timelineProps} ... />`
     */
    timelineProps: {
      open: isTimelineOpen,
      onOpenChange: onTimelineOpenChange,
      grantPermissionDialogProps: {
        open: permissionDialogState.open,
        onOpenChange: onPermissionDialogOpenChange,
        expiresAt: permissionDialogState.expiresAt,
      },
    },
  }
}
